import { useState, useMemo } from 'react'

// ── Data types ───────────────────────────────────────────────────────

interface Article {
  id: string
  title: string
  category: string
  summary: string
  content: string
  tags: string[]
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced'
  timeToComplete: string
}

interface Guide {
  id: string
  title: string
  description: string
  articleIds: string[]
  color: string
}

// ── Guides (curated learning paths) ─────────────────────────────────

const GUIDES: Guide[] = [
  {
    id: 'get-started-ticket-counting',
    title: 'Get Started with Ticket Counting',
    description: 'Add your first shows, input ticket counts, and understand your dashboard. Everything you need to replace spreadsheets with a single source of truth.',
    articleIds: ['adding_show_manually', 'inputting_ticket_counts', 'understanding_ticket_data'],
    color: '#9333ea',
  },
  {
    id: 'set-up-your-team',
    title: 'Set Up Your Team',
    description: 'Invite team members so everyone has access to the same ticket counts. No more duplicate emails or out-of-date spreadsheets.',
    articleIds: ['inviting_team_members', 'team_permissions'],
    color: '#8b5cf6',
  },
  {
    id: 'import-your-shows',
    title: 'Import Shows from CSV or Bandsintown',
    description: 'Already have tour data in a spreadsheet or on Bandsintown? Bulk import everything in minutes instead of re-entering it from scratch.',
    articleIds: ['importing_csv', 'connecting_bandsintown'],
    color: '#3b82f6',
  },
  {
    id: 'connect-your-calendar',
    title: 'Connect Your Calendar',
    description: 'Link Google Calendar so your shows and schedule stay in sync automatically. See everything in one unified view.',
    articleIds: ['google_calendar_sync', 'using_schedule_view'],
    color: '#f59e0b',
  },
  {
    id: 'reading-your-data',
    title: 'Reading Your Ticket Data',
    description: 'Understand percentage sold, ticket pace, capacity, and how to use your data to make better routing and marketing decisions.',
    articleIds: ['understanding_ticket_data', 'ticket_pace_routing'],
    color: '#10b981',
  },
]

// ── Articles ─────────────────────────────────────────────────────────

const ARTICLES: Article[] = [
  {
    id: 'adding_show_manually',
    title: 'Adding a Show Manually',
    category: 'Getting Started',
    summary: 'How to add a show with all the key details — artist, venue, date, capacity, and status',
    content: `**What it is:**

Adding a show manually lets you create a new entry in Dizee with all the details you need to track ticket counts for that event.

**How to do it:**

1. Go to the Shows page from the sidebar.
2. Click "Add Show" in the top right.
3. Select "Manual Add" from the options.
4. Fill in the required fields: Artist, Performance Date, City, State/Province, and Venue.
5. Optionally fill in Sellable Cap (venue capacity), Guarantee, Status, and other details.
6. Click "Create Show" to save.

**Tips:**

• The city field has auto-complete — start typing and it will suggest cities with auto-filled state/province.

• Set the Sellable Cap (venue capacity) so Dizee can automatically calculate your percentage sold when you enter ticket counts later.

• You can always edit a show after creating it by clicking the three-dot menu on any row in the table.

• The Status field (pending, confirmed, announced, private, canceled) helps you filter and organize your tour.`,
    tags: ['shows', 'manual', 'add', 'getting-started'],
    difficulty: 'Beginner',
    timeToComplete: '2-3 minutes',
  },
  {
    id: 'inputting_ticket_counts',
    title: 'Inputting and Updating Ticket Counts',
    category: 'Ticket Counting',
    summary: 'How to enter and update ticket counts for your shows — individually or in bulk',
    content: `**What it is:**

This is the core of Dizee's ticket counter. When you get a ticket count from a promoter, box office, or ticketing platform, you enter it here so your whole team can see it.

**How to update individual shows:**

1. Go to the Shows page.
2. Click "Update Tickets" in the top right.
3. You'll see all your upcoming shows listed.
4. Enter the ticket count and optionally the percentage sold.
5. Click "Save" next to the show to save that count.

**How to update in bulk:**

1. Click "Update Tickets" to open the ticket counter.
2. Enter counts for multiple shows.
3. Click "Save All" to update everything at once.

**Tips:**

• If you set a Sellable Cap for a show, the percentage sold auto-calculates when you enter a ticket count.

• The "Last 7 days" stat on the Shows dashboard shows total ticket activity, so you can see how actively counts are being tracked.

• Unsold Tickets gives you a quick view of remaining inventory across all shows that have capacity set.

• Each save records a timestamp so your team knows when the count was last updated.`,
    tags: ['tickets', 'counts', 'update', 'bulk', 'core'],
    difficulty: 'Beginner',
    timeToComplete: '1-2 minutes',
  },
  {
    id: 'understanding_ticket_data',
    title: 'Understanding Your Ticket Dashboard',
    category: 'Ticket Counting',
    summary: 'What each stat means and how to read your Shows page at a glance',
    content: `**What it is:**

The Shows page has a summary dashboard at the top with four key stats. Here's what each one means and how to use it.

**Unsold Tickets:**

Total remaining tickets across all shows that have a Sellable Cap set. This is calculated as (capacity - tickets sold) summed across your tour. Use this to understand total remaining inventory.

**Ticket Counts (Last 7 days):**

The total number of tickets sold across shows that had their counts updated in the last 7 days. This tells you how actively ticket counts are being tracked and the volume of recent sales.

**Upcoming Guarantees:**

The sum of all guarantee amounts for upcoming shows. This is your locked-in revenue from future performances.

**Next Up:**

Your nearest upcoming show by date. A quick reference so you always know what's next on the tour.

**The table columns:**

• Date — Performance date
• Venue — Where the show is
• City — Location
• Tickets — Current ticket count (tickets sold)
• Venue Capacity — Sellable cap for the venue
• % Sold — Percentage of capacity sold
• Status — Show status (pending, confirmed, announced, etc.)

**Tips:**

• Shows are sorted with upcoming shows first (soonest at top), then past shows.

• Use the search bar to quickly find shows by venue, city, or artist.

• Use the status filter dropdown to view only confirmed, pending, or other show types.`,
    tags: ['dashboard', 'stats', 'data', 'capacity', 'percentage'],
    difficulty: 'Beginner',
    timeToComplete: '5 minutes',
  },
  {
    id: 'importing_csv',
    title: 'Importing Shows from a CSV File',
    category: 'Importing Data',
    summary: 'Bulk upload tour dates from a CSV export — from your booking agency, spreadsheet, or any other source',
    content: `**What it is:**

If you already have tour dates in a spreadsheet, you can export them as a CSV and import them into Dizee in bulk. This saves you from manually entering each show.

**How to do it:**

1. Go to the Shows page and click "Add Show."
2. Select "CSV/Excel" from the options.
3. Click the upload area or drag your CSV file in.
4. Dizee will parse the file and match columns automatically.
5. Review the preview of matched rows.
6. Click "Import Shows" to save them all.

**Supported column names:**

Dizee auto-maps common column headers:
• Date / Perf Date / Show Date / Event Date → Performance Date
• Artist → Artist
• City, State → City, State
• City State / Location → Auto-splits into City + State
• Venue → Venue
• Event Title → Event Title
• Status → Status
• Sellable Cap / Capacity → Sellable Cap
• Tix Sold / Current Ticket Count → Tickets Sold
• % Tix Sold → Percentage Sold
• Guarantee → Guarantee
• Promoter → Promoter

**Tips:**

• You can upload .CSV or Excel (.xlsx / .xls) files. Both formats are fully supported.

• The importer handles common date formats: MM/DD/YYYY, YYYY-MM-DD, DD-MM-YYYY, and more.

• City-to-state auto-fill works during import — if you have city names, Dizee will try to fill in the state automatically.

• You can import multiple times. New shows are added alongside existing ones.`,
    tags: ['csv', 'import', 'spreadsheet', 'bulk', 'upload'],
    difficulty: 'Beginner',
    timeToComplete: '5-10 minutes',
  },
  {
    id: 'connecting_bandsintown',
    title: 'Connecting Bandsintown',
    category: 'Importing Data',
    summary: 'Pull in your tour dates automatically from Bandsintown using your App ID',
    content: `**What it is:**

If your tour dates are on Bandsintown, you can pull them directly into Dizee without re-entering anything. Dizee connects to Bandsintown's API using your App ID.

**How to do it:**

1. Go to the Shows page and click "Add Show."
2. Select "Bandsintown" from the options.
3. Enter your Bandsintown App ID (get this from artists.bandsintown.com).
4. Your artist name will be auto-detected from your profile, or you can enter it manually.
5. Click "Preview events" to see what will be imported.
6. Review the events and click "Import Events" to save them.

**Tips:**

• Dizee remembers your App ID after the first connection — you won't need to enter it again.

• A green "Connected" badge shows when your Bandsintown connection is active.

• Imported shows include venue, city, state, date, set time, and lineup information.

• You can disconnect and reconnect at any time.

• After import, you can edit any show to add ticket counts, capacity, and other details.`,
    tags: ['bandsintown', 'import', 'sync', 'api', 'events'],
    difficulty: 'Beginner',
    timeToComplete: '5 minutes',
  },
  {
    id: 'google_calendar_sync',
    title: 'Setting Up Google Calendar Sync',
    category: 'Calendar & Schedule',
    summary: 'Connect Google Calendar to see all your events alongside your shows in one place',
    content: `**What it is:**

Connecting Google Calendar lets you see all your calendar events alongside your shows in Dizee's Schedule view. This gives you a unified view of your tour schedule and other commitments.

**How to do it:**

1. Go to Settings from the sidebar.
2. Find the "Google Calendar" section.
3. Click "Connect Google Calendar."
4. Authorize Dizee to access your Google Calendar.
5. Once connected, your Google Calendar events will appear in the Schedule view.

**Tips:**

• The Schedule view merges shows, tasks, and Google Calendar events into one timeline.

• You can switch between list view and calendar view in the Schedule page.

• Google Calendar events are read-only in Dizee — edit them in Google Calendar and they'll sync automatically.

• You can disconnect Google Calendar at any time from Settings.`,
    tags: ['google', 'calendar', 'sync', 'schedule', 'integration'],
    difficulty: 'Beginner',
    timeToComplete: '3-5 minutes',
  },
  {
    id: 'using_schedule_view',
    title: 'Using the Schedule View',
    category: 'Calendar & Schedule',
    summary: 'Navigate the combined schedule that shows your tour dates, tasks, and calendar events together',
    content: `**What it is:**

The Schedule page combines all your shows, tasks, and Google Calendar events into one unified timeline. This is where you get the full picture of what's coming up.

**How to use it:**

1. Go to Schedule from the sidebar.
2. Toggle between "List" and "Calendar" view using the buttons at the top.
3. Use the date navigation to move between weeks or months.
4. Click on any item to see its details.

**What you'll see:**

• Shows appear with their venue, city, and status — color-coded by type.
• Google Calendar events (if connected) appear alongside your shows.
• The list view gives you a chronological timeline.
• The calendar view gives you a monthly overview.

**Tips:**

• Shows link directly to the Shows page where you can edit details or update ticket counts.

• The "Today" button quickly jumps back to the current date.

• Filter by type (shows, tasks, calendar events) to focus on what matters.`,
    tags: ['schedule', 'calendar', 'view', 'timeline', 'navigation'],
    difficulty: 'Beginner',
    timeToComplete: '3 minutes',
  },
  {
    id: 'inviting_team_members',
    title: 'Inviting Team Members',
    category: 'Team',
    summary: 'Add agents, managers, and other team members so everyone can access ticket counts',
    content: `**What it is:**

Adding team members to Dizee means everyone on your team can see the same ticket counts, show data, and schedule — without asking each other for updates.

**How to do it:**

1. Go to Team from the sidebar.
2. Click "Add Member" or "Invite" to add a new team member.
3. Enter their email address and set their role.
4. They'll receive an invitation to join.
5. Once they accept, they'll have access to the team's data based on their permissions.

**Tips:**

• Different roles have different access levels — set permissions based on what each team member needs.

• Team members can be agents, managers, or support staff.

• Everyone sees the same real-time data, so ticket counts are always in sync across your organization.

• You can remove team members or change their permissions at any time.`,
    tags: ['team', 'invite', 'members', 'collaboration', 'access'],
    difficulty: 'Beginner',
    timeToComplete: '3-5 minutes',
  },
  {
    id: 'team_permissions',
    title: 'Managing Team Permissions',
    category: 'Team',
    summary: 'Control what each team member can see and do within Dizee',
    content: `**What it is:**

Permissions let you control which sections of Dizee each team member can access. This ensures the right people have the right level of access.

**How to manage permissions:**

1. Go to Team from the sidebar.
2. Click on a team member to see their profile.
3. Adjust their section permissions (Shows, Schedule, Guides, Settings, etc.).
4. Save changes.

**Available sections:**

• Schedule — View and interact with the calendar and schedule
• Shows — View, add, edit shows and update ticket counts
• Guides — Access step-by-step guides and articles
• Team — View and manage team members
• Settings — Access account and integration settings

**Tips:**

• You can give a team member access to just Shows and Schedule if they only need to track ticket counts and dates.

• Managers might need full access, while support staff might only need to view shows.

• Permission changes take effect immediately.`,
    tags: ['permissions', 'roles', 'access', 'team', 'management'],
    difficulty: 'Intermediate',
    timeToComplete: '5 minutes',
  },
  {
    id: 'ticket_pace_routing',
    title: 'Using Ticket Data for Routing Decisions',
    category: 'Ticket Counting',
    summary: 'How to read ticket pace and use your data to make better tour routing and marketing decisions',
    content: `**What it is:**

Your ticket count data isn't just for record-keeping — it's a tool for making better decisions about your tour. Understanding ticket pace helps you identify which shows need more marketing push and which markets are strong.

**Key metrics to watch:**

• **% Sold** — Shows above 70% are tracking well. Shows below 40% with the show date approaching may need a marketing boost or promotional push.

• **Unsold Tickets** — High unsold counts on upcoming shows indicate where to focus marketing spend or promotional efforts.

• **Ticket Counts (Last 7 days)** — If this number is low, counts may not be getting updated regularly. Make sure your team is entering fresh data.

• **Venue Capacity vs. Tickets Sold** — Compare these to understand scale. 500 tickets at a 600-cap venue is very different from 500 at a 2,000-cap room.

**How to use this data:**

1. Sort your shows by date to focus on the most immediate upcoming shows.
2. Look at % sold for shows in the next 2-4 weeks.
3. Shows under 50% sold with less than 3 weeks out may need: social media pushes, radio/press outreach, ticket price adjustments, or added support acts.
4. Shows consistently selling well in certain markets tell you where to route future tours.

**Tips:**

• Check ticket counts at least twice a week for upcoming shows.

• Compare similar markets — if your artist sells 80% in one city and 30% in a comparable one, investigate why.

• Use the search and filter tools to focus on specific regions or time periods.

• Historical ticket data helps inform future tour routing decisions even after a tour ends.`,
    tags: ['pace', 'routing', 'marketing', 'decisions', 'analytics', 'strategy'],
    difficulty: 'Intermediate',
    timeToComplete: '10 minutes',
  },
]

// ── Helpers ──────────────────────────────────────────────────────────

const ARTICLES_BY_ID = Object.fromEntries(ARTICLES.map((a) => [a.id, a]))

const DIFFICULTY_COLORS: Record<string, string> = {
  Beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Intermediate: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Advanced: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

function estimateReadTime(content: string) {
  return Math.max(1, Math.ceil(content.split(/\s+/).length / 200))
}

function guideTime(guide: Guide): string {
  let total = 0
  for (const id of guide.articleIds) {
    const a = ARTICLES_BY_ID[id]
    if (!a) continue
    const t = a.timeToComplete
    if (t.includes('hour')) {
      const h = t.match(/(\d+)/)
      if (h) total += parseInt(h[1], 10) * 60
    } else {
      const nums = [...t.matchAll(/(\d+)/g)]
      if (nums.length) total += parseInt(nums[nums.length - 1][1], 10)
    }
  }
  if (total >= 60) return `${Math.round(total / 60)}+ hr`
  return `${total} min`
}

/** Render simple markdown-ish content into React nodes */
function renderContent(content: string) {
  return content.split('\n').map((line, i) => {
    const trimmed = line.trim()

    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      return (
        <h4 key={i} className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-5 mb-2 first:mt-0">
          {trimmed.slice(2, -2)}
        </h4>
      )
    }

    const numMatch = trimmed.match(/^(\d+)\.\s(.+)/)
    if (numMatch) {
      return (
        <div key={i} className="flex gap-3 mb-2 ml-1">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 text-xs font-medium flex items-center justify-center mt-0.5">
            {numMatch[1]}
          </span>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{numMatch[2]}</p>
        </div>
      )
    }

    if (trimmed.startsWith('•')) {
      return (
        <div key={i} className="flex gap-2 mb-1.5 ml-1">
          <span className="text-gray-400 dark:text-gray-500 mt-1 flex-shrink-0">&#8226;</span>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{trimmed.slice(1).trim()}</p>
        </div>
      )
    }

    if (trimmed.startsWith('- ')) {
      return (
        <p key={i} className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed ml-8 mb-1.5 italic">
          {trimmed.slice(2)}
        </p>
      )
    }

    if (!trimmed) return <div key={i} className="h-2" />

    return (
      <p key={i} className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-1.5">{trimmed}</p>
    )
  })
}

// ── Component ────────────────────────────────────────────────────────

export default function KnowledgePage() {
  const [search, setSearch] = useState('')
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null)
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)

  // Search results across all articles
  const searchResults = useMemo(() => {
    if (!search.trim()) return []
    const q = search.toLowerCase()
    return ARTICLES.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.summary.toLowerCase().includes(q) ||
        a.tags.some((t) => t.includes(q)) ||
        a.category.toLowerCase().includes(q),
    )
  }, [search])

  // Resolve articles for the selected guide
  const guideArticles = useMemo(() => {
    if (!selectedGuide) return []
    return selectedGuide.articleIds
      .map((id) => ARTICLES_BY_ID[id])
      .filter(Boolean)
  }, [selectedGuide])

  // Current article index within the guide (for prev/next nav)
  const articleIndexInGuide = useMemo(() => {
    if (!selectedGuide || !selectedArticle) return -1
    return selectedGuide.articleIds.indexOf(selectedArticle.id)
  }, [selectedGuide, selectedArticle])

  // ── Article Detail View ─────────────────────────────────────────

  if (selectedArticle) {
    const prevArticle = selectedGuide && articleIndexInGuide > 0
      ? ARTICLES_BY_ID[selectedGuide.articleIds[articleIndexInGuide - 1]]
      : null
    const nextArticle = selectedGuide && articleIndexInGuide >= 0 && articleIndexInGuide < selectedGuide.articleIds.length - 1
      ? ARTICLES_BY_ID[selectedGuide.articleIds[articleIndexInGuide + 1]]
      : null

    return (
      <div className="p-8 max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-6 flex-wrap">
          <button
            onClick={() => { setSelectedArticle(null); setSelectedGuide(null) }}
            className="hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            Guides
          </button>
          {selectedGuide && (
            <>
              <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <button
                onClick={() => setSelectedArticle(null)}
                className="hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
              >
                {selectedGuide.title}
              </button>
            </>
          )}
          <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-900 dark:text-gray-100 font-medium truncate">
            {selectedArticle.title}
          </span>
        </nav>

        {/* Step indicator (within a guide) */}
        {selectedGuide && articleIndexInGuide >= 0 && (
          <div className="flex items-center gap-2 mb-6">
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white"
              style={{ backgroundColor: selectedGuide.color }}
            >
              {articleIndexInGuide + 1}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Step {articleIndexInGuide + 1} of {selectedGuide.articleIds.length}
            </span>
          </div>
        )}

        {/* Article header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{selectedArticle.category}</span>
            <span className="text-gray-300 dark:text-gray-600">&#8226;</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${DIFFICULTY_COLORS[selectedArticle.difficulty]}`}>
              {selectedArticle.difficulty}
            </span>
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 mb-3">
            {selectedArticle.title}
          </h1>

          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
            {selectedArticle.summary}
          </p>

          <div className="flex items-center gap-4 text-xs text-gray-400 pb-5 border-b border-gray-200 dark:border-gray-700">
            <span>{estimateReadTime(selectedArticle.content)} min read</span>
            <span>{selectedArticle.timeToComplete}</span>
          </div>
        </div>

        {/* Article content */}
        <div className="mb-10">
          {renderContent(selectedArticle.content)}
        </div>

        {/* Previous / Next step navigation */}
        {selectedGuide && (prevArticle || nextArticle) && (
          <div className="flex items-stretch gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 mb-8">
            {prevArticle ? (
              <button
                onClick={() => setSelectedArticle(prevArticle)}
                className="flex-1 text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-white/[0.03] transition-all group"
              >
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1 block">Previous</span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                  {prevArticle.title}
                </span>
              </button>
            ) : <div className="flex-1" />}
            {nextArticle ? (
              <button
                onClick={() => setSelectedArticle(nextArticle)}
                className="flex-1 text-right px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-white/[0.03] transition-all group"
              >
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1 block">Next</span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                  {nextArticle.title}
                </span>
              </button>
            ) : <div className="flex-1" />}
          </div>
        )}

        {/* Tags */}
        {selectedArticle.tags.length > 0 && (
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Tags</h4>
            <div className="flex flex-wrap gap-2">
              {selectedArticle.tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => {
                    setSelectedArticle(null)
                    setSelectedGuide(null)
                    setSearch(tag)
                  }}
                  className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Guide / Path View (stepper) ───────────────────────────────────

  if (selectedGuide) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <button
          onClick={() => setSelectedGuide(null)}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors mb-8 group"
        >
          <svg className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Guides
        </button>

        {/* Guide header */}
        <div className="mb-10">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 mb-4">
            {selectedGuide.title}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">
            {selectedGuide.description}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500 mt-3">
            <span>{selectedGuide.articleIds.length} {selectedGuide.articleIds.length === 1 ? 'step' : 'steps'}</span>
            <span>{guideTime(selectedGuide)}</span>
          </div>
        </div>

        {/* Vertical stepper */}
        <div className="relative">
          {guideArticles.map((article, idx) => {
            const isLast = idx === guideArticles.length - 1
            return (
              <div key={article.id} className="relative flex gap-5">
                {/* Timeline connector + circle */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setSelectedArticle(article)}
                    className="relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 transition-transform hover:scale-110"
                    style={{ backgroundColor: selectedGuide.color }}
                  >
                    {idx + 1}
                  </button>
                  {!isLast && (
                    <div
                      className="w-0.5 flex-1 min-h-[24px]"
                      style={{ backgroundColor: selectedGuide.color + '30' }}
                    />
                  )}
                </div>

                {/* Step card */}
                <button
                  onClick={() => setSelectedArticle(article)}
                  className={`flex-1 text-left rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all group bg-white dark:bg-gray-800 ${isLast ? 'mb-0' : 'mb-3'}`}
                >
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-300 mb-1.5">
                    {article.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-3 line-clamp-2">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${DIFFICULTY_COLORS[article.difficulty]}`}>
                      {article.difficulty}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      {estimateReadTime(article.content)} min read
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      {article.timeToComplete}
                    </span>
                  </div>
                </button>
              </div>
            )
          })}
        </div>

        {/* Start button */}
        {guideArticles.length > 0 && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setSelectedArticle(guideArticles[0])}
              className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90 hover:shadow-md"
              style={{ backgroundColor: selectedGuide.color }}
            >
              Start with Step 1
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── Home View (Guides + Search) ───────────────────────────────────

  const featured = GUIDES[0]
  const rest = GUIDES.slice(1)

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight mb-2 text-gray-900 dark:text-gray-100">Guides</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Step-by-step paths to get your ticket counting set up. Pick a guide and follow along.
        </p>
      </div>

      {/* Search bar */}
      <div className="relative mb-10">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search all articles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-gray-400/20 focus:border-gray-300 dark:focus:border-gray-500 transition-all placeholder:text-gray-400 dark:text-gray-200"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search results (when searching) */}
      {search.trim() ? (
        <div>
          <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'}
          </h2>
          {searchResults.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                No articles match &ldquo;{search}&rdquo;.
              </p>
              <button
                onClick={() => setSearch('')}
                className="text-sm text-gray-700 dark:text-gray-300 underline underline-offset-2 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
              {searchResults.map((article) => (
                <button
                  key={article.id}
                  onClick={() => {
                    setSelectedArticle(article)
                    const parentGuide = GUIDES.find((g) => g.articleIds.includes(article.id))
                    setSelectedGuide(parentGuide ?? null)
                  }}
                  className="w-full text-left px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-white/[0.04] transition-colors group flex items-start gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-300 mb-1">
                      {article.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-2 line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${DIFFICULTY_COLORS[article.difficulty]}`}>
                        {article.difficulty}
                      </span>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {estimateReadTime(article.content)} min read
                      </span>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {article.timeToComplete}
                      </span>
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Featured guide (hero) */}
          <button
            onClick={() => setSelectedGuide(featured)}
            className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-6 transition-all hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm group relative overflow-hidden bg-white dark:bg-gray-800"
          >
            {/* Subtle accent gradient */}
            <div
              className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
              style={{ background: `linear-gradient(135deg, ${featured.color} 0%, transparent 60%)` }}
            />
            <div className="relative">
              <div className="mb-4">
                <span className="text-[10px] uppercase tracking-wider font-medium text-gray-400 dark:text-gray-500 block mb-1">Recommended</span>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                  {featured.title}
                </h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4 max-w-2xl">
                {featured.description}
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-400 dark:text-gray-500">{featured.articleIds.length} steps</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{guideTime(featured)}</span>
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium transition-colors"
                  style={{ color: featured.color }}
                >
                  Start guide
                  <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </div>
          </button>

          {/* Other guides grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rest.map((guide) => (
              <button
                key={guide.id}
                onClick={() => setSelectedGuide(guide)}
                className="text-left rounded-xl border border-gray-200 dark:border-gray-700 p-5 transition-all hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm group relative overflow-hidden bg-white dark:bg-gray-800"
              >
                <div
                  className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
                  style={{ background: `linear-gradient(135deg, ${guide.color} 0%, transparent 60%)` }}
                />
                <div className="relative">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors mb-3">
                    {guide.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-3 line-clamp-2">
                    {guide.description}
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">{guide.articleIds.length} {guide.articleIds.length === 1 ? 'step' : 'steps'}</span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">{guideTime(guide)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
